import React, { useEffect, useState } from 'react';
import VariableRequest from '@requests/global_variables';

import { Button, LoadingButton, Modal } from '@common/index';
import { IconButton, Stack, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, State, flowActions, FlowGlobalVariableModalVariant } from '@store/index';
import { useTheme } from '../../styles/theme';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { Label } from '@common/Label';
import { DataObjectOutlined, EditOutlined } from '@mui/icons-material';
import { GlobalVariableState, GlobalVariableType } from '@components/Workspace/routes/GlobalVariables/types';
import { convertVariableName } from './AddDataPrefix';

const initialGlobalVariable: GlobalVariableState = {
  id: '',
  key: '',
  value: '',
  type: GlobalVariableType.STRING,
};

export const ReadCreateEditGlobalVariable: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();

  const { service_id } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [globalVariable, setGlobalVariable] = useState<GlobalVariableState>(initialGlobalVariable);
  const resetGlobalVariable = () => setGlobalVariable(initialGlobalVariable);

  const globalVariableModal = useSelector((state: State) => state.flow.globalVariableModal);

  const isReadMode = globalVariableModal?.type === FlowGlobalVariableModalVariant.READ;
  const isCreateMode = globalVariableModal?.type === FlowGlobalVariableModalVariant.CREATE;
  const isEditMode = globalVariableModal?.type === FlowGlobalVariableModalVariant.EDIT;

  const isOpen = globalVariableModal !== null && (isReadMode || isCreateMode || isEditMode);

  useEffect(() => {
    if (!globalVariableModal) return;

    switch (globalVariableModal.type) {
      case FlowGlobalVariableModalVariant.READ:
        const readData = globalVariableModal.data;
        setGlobalVariable((prev) => ({ ...prev, ...readData }));
        break;
      case FlowGlobalVariableModalVariant.EDIT:
        const editData = globalVariableModal.data;
        setGlobalVariable((prev) => ({ ...prev, ...editData }));
        break;
      case FlowGlobalVariableModalVariant.CREATE:
        resetGlobalVariable();
        break;
      default:
        break;
    }
  }, [globalVariableModal]);

  const { mutateAsync: createGlobalVariable, isPending: loadingWhileCreation } = useMutation({
    mutationFn: VariableRequest.createVariable,
    onSuccess: (data) => {
      if (!data) return;
      queryClient.invalidateQueries({ queryKey: ['globalVariables', service_id] });
      enqueueSnackbar('Global Variable created successfully!', { variant: 'success', autoHideDuration: 3000 });
      closeModal();
    },
    onError: (error) => {
      console.error(error);
      enqueueSnackbar('Failed to create Global Variable!', {
        variant: 'error',
        autoHideDuration: 10000,
      });

      enqueueSnackbar(error.message, {
        variant: 'error',
        autoHideDuration: 10000,
      });
    },
  });

  const { mutateAsync: updateGlobalVariable, isPending: loadingWhileUpdating } = useMutation({
    mutationFn: VariableRequest.updateVariable,
    onSuccess: (data) => {
      if (!data) return;
      queryClient.invalidateQueries({ queryKey: ['globalVariables', service_id] });
      enqueueSnackbar('Global Variable updated successfully!', { variant: 'success', autoHideDuration: 3000 });
      closeModal();
    },
    onError: (error) => {
      console.error(error);
      enqueueSnackbar('Failed to update Global Variable!', {
        variant: 'error',
        autoHideDuration: 10000,
      });

      enqueueSnackbar(error.message, {
        variant: 'error',
        autoHideDuration: 10000,
      });
    },
  });

  const isLoading = loadingWhileCreation || loadingWhileUpdating;

  const closeModal = () => {
    resetGlobalVariable();
    dispatch(flowActions.setFlowGlobalVariableModal(null));
  };

  const changeToEditMode = () =>
    dispatch(
      flowActions.setFlowGlobalVariableModal({
        type: FlowGlobalVariableModalVariant.EDIT,
        data: globalVariable,
      })
    );

  const handleSaveOrUpdate = async () => {
    if (!globalVariable) return;

    const data = {
      key: globalVariable.key,
      value: globalVariable.value,
      type: globalVariable.type,
    };

    try {
      if (isCreateMode) {
        await createGlobalVariable({ serviceId: service_id, globalVariable: data });
      } else if (isEditMode) {
        await updateGlobalVariable({ serviceId: service_id, id: globalVariable.id, globalVariable: data });
      }
    } catch {}
  };

  return (
    <Modal
      open={isOpen}
      setOpen={closeModal}
      header='Create a Global Variable'
      header_actions={
        globalVariableModal?.type === FlowGlobalVariableModalVariant.READ && (
          <IconButton onClick={changeToEditMode}>
            <EditOutlined />
          </IconButton>
        )
      }
      background={theme.primary}
      icon={<DataObjectOutlined />}
      info='Create a global variable that will be accessible in all expressions editors across all workflows in your workspace. Useful for defining system messages or argument descriptions.'
      body={
        <Stack spacing={3}>
          <Stack spacing='8px'>
            <Label
              title='Variable Name'
              tooltip='This is the name of your global variable. You can access it from workflows using {{ global.variable.[NAME] }}.'
            />
            <TextField
              variant='outlined'
              value={globalVariable.key}
              onChange={(e) => setGlobalVariable((prev) => ({ ...prev, key: convertVariableName(e.target.value) }))}
              placeholder='Name'
              fullWidth
              disabled={isReadMode}
            />
          </Stack>
          <Stack spacing='8px'>
            <Label
              title='Variable Value'
              tooltip='This is the value that will be used to replace the global variable when used in your workflows.'
            />
            <TextField
              variant='outlined'
              value={globalVariable.value}
              onChange={(e) => setGlobalVariable((prev) => ({ ...prev, value: e.target.value }))}
              placeholder='Value'
              fullWidth
              disabled={isReadMode}
              aria-disabled={isReadMode}
              minRows={4}
              maxRows={12}
              multiline
            />
          </Stack>
        </Stack>
      }
      actions={
        <Stack spacing={1} direction='row'>
          <Button size='medium' onClick={closeModal}>
            Close
          </Button>
          {(isEditMode || isCreateMode) && (
            <LoadingButton
              size='medium'
              customColor='primary'
              minWidth='75px'
              onClick={handleSaveOrUpdate}
              disabled={isLoading}
              loading={isLoading}
            >
              {isEditMode ? 'Save' : 'Create'}
            </LoadingButton>
          )}
        </Stack>
      }
    />
  );
};
